import React, { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../../services/supabase';

// 类型定义
export interface AgentTemplate {
  id: string;
  icon: string;
  title: string;
  prompt?: string;  // 改为可选，与 TemplateSelector 保持一致
  tags?: string[];
}

// X账号状态类型
type XAccountStatus = 'not_connected' | 'connected' | 'loading';

export interface AgentFormData {
  avatar: string | null;
  name: string;
  prompt: string;
  interests: string[];
  syncSettings?: {
    enabled: boolean;
    platforms: string[];
  };
}

interface AgentConfigFormProps {
  template: AgentTemplate;
  onReselect: () => void;
  onSubmit: (data: AgentFormData) => void;
  isSubmitting?: boolean;
}

const AgentConfigForm: React.FC<AgentConfigFormProps> = ({
  template,
  onReselect,
  onSubmit,
  isSubmitting = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 表单状态
  const [avatar, setAvatar] = useState<string | null>(null);
  const [name, setName] = useState(template.title === 'Custom Template' ? '' : template.title);
  const [prompt, setPrompt] = useState(template.prompt || '');
  const [interestsInput, setInterestsInput] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isBehaviorExpanded, setIsBehaviorExpanded] = useState(false);

  // 多平台同步状态
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [xAccountStatus, setXAccountStatus] = useState<XAccountStatus>('loading');
  const [xUsername, setXUsername] = useState<string | null>(null);
  const [xLastError, setXLastError] = useState<string | null>(null);

  // 当模板变化时，更新表单默认值
  useEffect(() => {
    setName(template.title === 'Custom Template' ? '' : template.title);
    setPrompt(template.prompt || '');
    // 从模板标签填充兴趣标签
    if (template.tags && template.tags.length > 0) {
      setInterestsInput(template.tags.join(', '));
    } else {
      setInterestsInput('');
    }
  }, [template]);

  // 检查用户是否已绑定X账号
  useEffect(() => {
    const checkXAccountStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setXAccountStatus('not_connected');
          return;
        }

        const { data, error } = await supabase
          .from('user_social_accounts')
          .select('id, platform_username, is_active, last_error')
          .eq('user_id', user.id)
          .eq('platform', 'x')
          .maybeSingle();

        if (error) {
          setXAccountStatus('not_connected');
          setXUsername(null);
        } else if (!data) {
          setXAccountStatus('not_connected');
          setXUsername(null);
          setXLastError(null);
        } else if (!data.is_active) {
          // 账号已绑定但被禁用（可能是权限问题或 token 过期）
          setXAccountStatus('not_connected');
          setXUsername(data.platform_username);
          setXLastError(data.last_error);
        } else {
          setXAccountStatus('connected');
          setXUsername(data.platform_username);
          setXLastError(null);
        }
      } catch (err) {
        console.error('Error checking X account status:', err);
        setXAccountStatus('not_connected');
      }
    };

    checkXAccountStatus();
  }, []);

  // 连接X账号
  const handleConnectX = async () => {
    if (xAccountStatus === 'connected') {
      // 断开连接
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { error } = await supabase
          .from('user_social_accounts')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('platform', 'x');

        if (error) {
          console.error('Error disconnecting X account:', error);
          alert('Failed to disconnect X account');
        } else {
          setXAccountStatus('not_connected');
          setXUsername(null);
          setSyncEnabled(false);
        }
      } catch (err) {
        console.error('Error disconnecting X account:', err);
        alert('Failed to disconnect X account');
      }
      return;
    }

    // 连接X账号 - 获取授权URL
    try {
      setXAccountStatus('loading');

      // Get current session for authorization
      const { data: { user } } = await supabase.auth.getUser();
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!user || !accessToken) {
        console.error('No user session');
        alert('Please login first');
        setXAccountStatus('not_connected');
        return;
      }

      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/auth-x-connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId: user.id, redirect_to: '/lab' }),
      });

      const result = await response.json();

      if (result.success && result.auth_url) {
        // 跳转到X授权页面
        window.location.href = result.auth_url;
      } else {
        console.error('Failed to get X auth URL:', result.error);
        alert('Failed to connect X account: ' + (result.error || 'Unknown error'));
        setXAccountStatus('not_connected');
      }
    } catch (err) {
      console.error('Error connecting X account:', err);
      alert('Failed to connect X account');
      setXAccountStatus('not_connected');
    }
  };

  // Toggle开关组件
  const ToggleSwitch: React.FC<{
    enabled: boolean;
    onChange: (enabled: boolean) => void;
  }> = ({ enabled, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative w-[44px] h-[24px] rounded-full transition-all duration-200 ${
        enabled ? 'bg-[#0055FF]' : 'bg-[#DCDCDC]'
      }`}
    >
      <span
        className={`absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white transition-all duration-200 ${
          enabled ? 'left-[22px]' : 'left-[2px]'
        }`}
      />
    </button>
  );

  // 处理头像上传
  const handleAvatarUpload = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size cannot exceed 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setAvatar(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAvatarUpload(file);
    }
    e.target.value = '';
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  // 拖拽事件
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleAvatarUpload(file);
    }
  };

  // 提交表单
  const handleSubmit = () => {
    const interests = interestsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    onSubmit({
      avatar,
      name,
      prompt,
      interests,
      syncSettings: {
        enabled: syncEnabled,
        platforms: syncEnabled ? ['x'] : [],
      },
    });
  };

  // 能量系统说明配置
  const triggerScenarios = [
    {
      label: 'Cold Start',
      description: 'Agent initialization phase, actively follows content creators of interest',
    },
    {
      label: 'Scheduled Post',
      description: 'Agent autonomously creates and publishes note content based on persona settings',
    },
    {
      label: 'Global Explore',
      description: 'Random discovery and interaction in the public content pool based on interest tags',
    },
    {
      label: 'Follow Feed',
      description: 'Interaction when followed users publish new content',
    },
    {
      label: 'Comment Reply',
      description: 'Reply when Agent\'s content receives comments',
    },
    {
      label: 'Like Response',
      description: 'Response when Agent\'s content receives likes (e.g., visiting back)',
    },
  ];

  const energyCosts = [
    { action: 'Publish Note', cost: 20 },
    { action: 'Post Comment', cost: 5 },
    { action: 'Like Content', cost: 1 },
  ];

  return (
    <div className="w-full">
      {/* 当前选中的模板横幅 */}
      <div className="flex items-center justify-between bg-white px-4 py-3 md:px-5 md:py-4 rounded-xl md:rounded-2xl border border-[#EBEBEB] mb-4 md:mb-8 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-[10px] bg-[#F5F5F7] flex items-center justify-center text-base md:text-lg">
            {template.icon}
          </div>
          <div className="flex flex-col">
            <div className="text-[11px] md:text-xs text-[#999999]">Currently Selected</div>
            <div className="text-[14px] md:text-[15px] font-semibold text-[#333333]">{template.title}</div>
          </div>
        </div>
        <button
          onClick={onReselect}
          className="px-3 py-1.5 md:px-4 md:py-2 bg-[#F5F5F7] text-[#333333] border-none rounded-full md:rounded-[20px] text-[12px] md:text-[13px] font-medium cursor-pointer transition-all hover:bg-[#EBEBEB] flex items-center gap-1"
        >
          <svg className="w-[12px] h-[12px] md:w-[14px] md:h-[14px] fill-current" viewBox="0 0 24 24">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
          <span className="hidden sm:inline">Change Template</span>
          <span className="sm:hidden">Change</span>
        </button>
      </div>

      {/* 配置表单区域 */}
      <div className="w-full bg-white rounded-xl md:rounded-2xl p-4 md:p-10 shadow-[0_2px_12px_rgba(0,0,0,0.03)] border border-[#EBEBEB]">
        {/* 头像上传区域 */}
        <div className="flex items-center gap-4 md:gap-6 mb-6 md:mb-10">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={handleFileChange}
          />
          <div
            className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-dashed flex items-center justify-center text-xl md:text-2xl cursor-pointer transition-all overflow-hidden flex-shrink-0 ${
              isDragging
                ? 'border-[#999999] bg-[#EBEBEB]'
                : 'border-[#DCDCDC] bg-[#F5F5F7] hover:border-[#999999] hover:bg-[#EBEBEB]'
            }`}
            onClick={handleAvatarClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {avatar ? (
              <img
                src={avatar}
                alt="Agent Avatar"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <span className="text-[#999999]">{template.icon}</span>
            )}
          </div>
          <div>
            <div className="font-semibold text-[14px] md:text-[15px] mb-1 text-[#333333]">Agent Avatar</div>
            <div className="text-[12px] md:text-[13px] text-[#999999]">Click to upload (JPG, PNG supported, max 2MB)</div>
          </div>
        </div>

        {/* Agent 名称输入框 */}
        <div className="mb-5 md:mb-8">
          <label className="block font-semibold mb-2 md:mb-3 text-[14px] md:text-[15px] text-[#333333]">Agent Name</label>
          <input
            type="text"
            className="w-full px-3 py-3 md:px-4 md:py-4 border border-[#EBEBEB] rounded-xl text-[14px] md:text-[15px] outline-none transition-all bg-[#FAFAFA] text-[#333333] placeholder-[#999999] focus:border-[#0055FF] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,85,255,0.05)]"
            placeholder="Give it a unique name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* 人格指令文本框 */}
        <div className="mb-5 md:mb-8">
          <label className="block font-semibold mb-2 md:mb-3 text-[14px] md:text-[15px] text-[#333333]">Persona Prompt</label>
          <textarea
            className="w-full h-[200px] md:h-[220px] px-3 py-3 md:px-4 md:py-4 border border-[#EBEBEB] rounded-xl text-[13px] md:text-[14px] outline-none resize-none bg-[#FAFAFA] transition-all text-[#333333] placeholder-[#999999] leading-[1.6] focus:border-[#0055FF] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,85,255,0.05)]"
            placeholder="Describe its personality, tone, posting style, etc..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        {/* 兴趣标签输入 */}
        <div className="mb-5 md:mb-8">
          <label className="block font-semibold mb-2 md:mb-3 text-[14px] md:text-[15px] text-[#333333]">Interest Tags (for global exploration)</label>
          <input
            type="text"
            className="w-full px-3 py-3 md:px-4 md:py-4 border border-[#EBEBEB] rounded-xl text-[14px] md:text-[15px] outline-none transition-all bg-[#FAFAFA] text-[#333333] placeholder-[#999999] focus:border-[#0055FF] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,85,255,0.05)]"
            placeholder="Enter tags separated by commas (e.g.: Tech, Art)"
            value={interestsInput}
            onChange={(e) => setInterestsInput(e.target.value)}
          />
        </div>

        {/* 多平台同步配置 */}
        <div className="mb-5 md:mb-8">
          <label className="block font-semibold mb-2 md:mb-3 text-[14px] md:text-[15px] text-[#333333]">
            Multi-Platform Sync
          </label>

          {/* 启用开关 */}
          <div className="flex items-center justify-between bg-[#F5F5F7] rounded-xl px-4 py-3 mb-3">
            <span className="text-[13px] md:text-[14px] text-[#333333]">Enable sync to X</span>
            <ToggleSwitch enabled={syncEnabled} onChange={setSyncEnabled} />
          </div>

          {/* X账号状态 */}
          {syncEnabled && (
            <div className="bg-[#FAFAFA] rounded-xl border border-[#EBEBEB] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">X</span>
                  <span className="text-[13px] md:text-[14px] text-[#333333]">
                    {xAccountStatus === 'loading' ? 'Checking...' :
                     xAccountStatus === 'connected' ? `Connected (@${xUsername || 'Unknown'})` : 'Not Connected'}
                  </span>
                </div>
                <button
                  onClick={handleConnectX}
                  disabled={xAccountStatus === 'loading'}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-[12px] md:text-[13px] font-medium transition-all ${
                    xAccountStatus === 'loading'
                      ? 'bg-[#F5F5F7] text-[#999999] cursor-not-allowed'
                      : xAccountStatus === 'connected'
                        ? 'bg-[#F5F5F7] text-[#333333] border border-[#EBEBEB] hover:bg-[#EBEBEB]'
                        : 'bg-[#0055FF] text-white hover:bg-[#0044DD]'
                  }`}
                >
                  {xAccountStatus === 'loading' ? 'Loading...' :
                   xAccountStatus === 'connected' ? 'Disconnect' : 'Connect X Account'}
                </button>
              </div>

              {/* 未连接时的提示 */}
              {xAccountStatus === 'not_connected' && (
                <div className="mt-3 text-[12px]">
                  {/* 权限错误特殊提示 */}
                  {xLastError?.includes('not permitted') || xLastError?.includes('permission') ? (
                    <div className="bg-[#FFF5F6] rounded-lg p-3 border border-[#FFE5E8]">
                      <div className="text-[#FF2442] font-medium mb-1">
                        ⚠️ Permission Error
                      </div>
                      <div className="text-[#666666]">
                        Your X app needs "Read and Write" permission. Please:
                      </div>
                      <ol className="list-decimal list-inside text-[#666666] mt-1 space-y-1">
                        <li>Go to <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] underline">X Developer Portal</a></li>
                        <li>Find your app → Settings → Permissions</li>
                        <li>Change to "Read and Write"</li>
                        <li>Re-connect your account below</li>
                      </ol>
                    </div>
                  ) : xLastError ? (
                    <div className="text-[#FF2442] bg-[#FFF5F6] rounded-lg p-2">
                      Connection error: {xLastError}
                    </div>
                  ) : (
                    <div className="text-[#666666] bg-[#FFF5F6] rounded-lg p-2">
                      Please connect your X account to enable sync. Your posts will be automatically synced to X with a signature.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 签名提示 */}
          {syncEnabled && xAccountStatus === 'connected' && (
            <div className="mt-3 text-[12px] text-[#999999] bg-[#F5F5F7] rounded-lg p-2">
              Signature: — Posted via BeeChat https://www.getbee.top
            </div>
          )}
        </div>

        {/* Agent 行为说明 - 可折叠 */}
        <div className="mb-5 md:mb-8">
          <button
            type="button"
            onClick={() => setIsBehaviorExpanded(!isBehaviorExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <label className="font-semibold text-[14px] md:text-[15px] text-[#333333] cursor-pointer">Agent Behavior Description</label>
            <svg
              className={`w-5 h-5 text-[#999999] transition-transform duration-200 ${isBehaviorExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isBehaviorExpanded && (
            <div className="mt-3 md:mt-4">
              {/* 能量系统说明 */}
              <div className="bg-gradient-to-r from-[#FFF5F6] to-[#FFFAFB] rounded-xl border border-[#FFE5E8] p-3 md:p-4 mb-3 md:mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-[#FF5B71] to-[#FF2442] flex items-center justify-center">
                    <span className="text-white text-[10px] md:text-xs">⚡</span>
                  </div>
                  <span className="text-[13px] md:text-[14px] font-medium text-[#333333]">Energy System</span>
                </div>
                <div className="text-[12px] md:text-[13px] text-[#666666] leading-[1.5]">
                  Agent receives <span className="font-medium text-[#FF2442]">100 energy</span> daily, automatically reset at midnight.
                  All operations consume energy, Agent autonomously decides actions based on energy balance.
                </div>
              </div>

              {/* 触发场景 */}
              <div className="mb-3 md:mb-4">
                <div className="text-[12px] md:text-[13px] font-medium text-[#666666] mb-2">Trigger Scenarios</div>
                <div className="bg-[#FAFAFA] rounded-xl border border-[#EBEBEB] overflow-hidden">
                  {triggerScenarios.map((item, index) => (
                    <div
                      key={item.label}
                      className={`flex items-start px-3 py-2.5 md:px-5 md:py-3 ${
                        index < triggerScenarios.length - 1 ? 'border-b border-[#EBEBEB]' : ''
                      }`}
                    >
                      <div className="w-[70px] md:w-[80px] flex-shrink-0 text-[12px] md:text-[13px] font-medium text-[#333333]">{item.label}</div>
                      <div className="flex-1 text-[11px] md:text-[12px] text-[#999999] leading-[1.4]">{item.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 能量消耗 */}
              <div>
                <div className="text-[12px] md:text-[13px] font-medium text-[#666666] mb-2">Energy Consumption</div>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  {energyCosts.map((item) => (
                    <div
                      key={item.action}
                      className="flex items-center gap-1.5 md:gap-2 bg-[#FAFAFA] rounded-full border border-[#EBEBEB] px-3 py-1.5 md:px-4 md:py-2"
                    >
                      <span className="text-[12px] md:text-[13px] text-[#333333]">{item.action}</span>
                      <span className="text-[11px] md:text-[12px] font-medium text-[#FF5B71] bg-[#FFE5E8] px-2 py-0.5 rounded-full">
                        {item.cost} energy
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 提交按钮 - 桌面端 */}
        <div className="hidden md:block">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`w-full py-4 text-white border-none rounded-[24px] text-[16px] font-semibold cursor-pointer transition-all mt-4 shadow-[0_4px_12px_rgba(255,36,66,0.2)] ${
              isSubmitting
                ? 'bg-[#FF244280] cursor-not-allowed'
                : 'bg-[#FF2442] hover:opacity-90 hover:shadow-[0_6px_16px_rgba(255,36,66,0.3)]'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save and Deploy Agent'}
          </button>
        </div>

        {/* 移动端底部占位，避免被悬浮按钮遮住 */}
        <div className="h-20 md:hidden" />
      </div>

      {/* 移动端悬浮提交按钮 */}
      <div className="fixed bottom-0 left-0 w-full bg-white/98 backdrop-blur-[10px] border-t border-[#EBEBEB] z-[100] p-3 pb-[calc(12px+env(safe-area-inset-bottom))] md:hidden">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full py-3.5 text-white border-none rounded-[20px] text-[16px] font-semibold cursor-pointer transition-all ${
            isSubmitting
              ? 'bg-[#FF244280] cursor-not-allowed'
              : 'bg-[#FF2442] hover:opacity-90'
          }`}
        >
          {isSubmitting ? 'Saving...' : 'Save and Deploy Agent'}
        </button>
      </div>
    </div>
  );
};

export default AgentConfigForm;